import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar as CalendarIcon,
  Edit,
  Trash2,
  Loader2,
  Sparkles,
  MessageSquare,
  Send,
  ExternalLink,
  CheckCircle2
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "New Lead", color: "bg-blue-900/50 text-blue-400 border-blue-800" },
  { value: "In Follow-Up", color: "bg-purple-900/50 text-purple-400 border-purple-800" },
  { value: "Warm", color: "bg-orange-900/50 text-orange-400 border-orange-800" },
  { value: "Hot", color: "bg-red-900/50 text-red-400 border-red-800" },
  { value: "Converted", color: "bg-green-900/50 text-green-400 border-green-800" },
  { value: "Lost", color: "bg-slate-800/50 text-slate-400 border-slate-700" },
];

const CHANNEL_OPTIONS = ["Email", "WhatsApp", "Call", "In Person", "LinkedIn", "SMS"];

const ProspectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateStreak } = useAuth();
  const [prospect, setProspect] = useState(null);
  const [techniques, setTechniques] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [techniqueDialogOpen, setTechniqueDialogOpen] = useState(false);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [saving, setSaving] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [logFormData, setLogFormData] = useState({
    channel: "WhatsApp",
    notes: "",
  });
  const [nextFollowUpDate, setNextFollowUpDate] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [prospectRes, techniquesRes, followupsRes] = await Promise.all([
        axios.get(`${API}/prospects/${id}`),
        axios.get(`${API}/techniques`),
        axios.get(`${API}/followups/${id}`),
      ]);
      setProspect(prospectRes.data);
      setTechniques(techniquesRes.data);
      setFollowups(followupsRes.data);
      setEditFormData(prospectRes.data);
      if (prospectRes.data.follow_up_by) {
        setFollowUpDate(new Date(prospectRes.data.follow_up_by));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load prospect");
      navigate("/prospects");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editFormData,
        follow_up_by: followUpDate ? format(followUpDate, "yyyy-MM-dd") : null,
      };
      await axios.put(`${API}/prospects/${id}`, payload);
      toast.success("Prospect updated!");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update prospect");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/prospects/${id}`);
      toast.success("Prospect deleted");
      navigate("/prospects");
    } catch (error) {
      toast.error("Failed to delete prospect");
    }
  };

  const handleLogFollowup = async () => {
    if (!selectedTechnique) return;
    setSaving(true);
    try {
      const payload = {
        prospect_id: id,
        technique_id: selectedTechnique.id,
        channel: logFormData.channel,
        notes: logFormData.notes,
        next_follow_up_date: nextFollowUpDate
          ? format(nextFollowUpDate, "yyyy-MM-dd")
          : null,
      };
      const response = await axios.post(`${API}/followups`, payload);
      toast.success("Follow-up logged! 🔥");
      setTechniqueDialogOpen(false);
      setSelectedTechnique(null);
      setLogFormData({ channel: "WhatsApp", notes: "" });
      setNextFollowUpDate(null);
      fetchData();
      // Update streak
      const statsRes = await axios.get(`${API}/dashboard/stats`);
      updateStreak(statsRes.data.streak);
      
      // Update daily challenge progress
      try {
        const challengeRes = await axios.post(`${API}/challenges/update-progress`);
        if (challengeRes.data.completed && challengeRes.data.points_earned > 0) {
          toast.success(`Challenge Complete! +${challengeRes.data.points_earned} points! 🏆`);
        }
      } catch (e) {
        // Challenge update is non-critical
      }
    } catch (error) {
      toast.error("Failed to log follow-up");
    } finally {
      setSaving(false);
    }
  };

  const getAISuggestion = async () => {
    setLoadingAI(true);
    try {
      const response = await axios.post(`${API}/ai/suggest-followup`, {
        prospect_id: id,
        context: "",
      });
      setAiSuggestion(response.data);
    } catch (error) {
      console.error("AI error:", error);
      toast.error("Could not get AI suggestion");
    } finally {
      setLoadingAI(false);
    }
  };

  const openWhatsApp = (template) => {
    if (!prospect?.phone) {
      toast.error("No phone number available");
      return;
    }
    const phone = prospect.phone.replace(/[^0-9]/g, "");
    const message = template.replace(/{name}/g, prospect.name);
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const openEmail = (template) => {
    if (!prospect?.email) {
      toast.error("No email available");
      return;
    }
    const subject = `Following up - ${prospect.name}`;
    const body = template.replace(/{name}/g, prospect.name);
    const url = `mailto:${prospect.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find((s) => s.value === status);
    return option?.color || "bg-slate-800 text-slate-400";
  };

  const getTechniqueIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />;
  };

  const getTechniqueById = (techId) => techniques.find((t) => t.id === techId);

  const unusedTechniques = techniques.filter(
    (t) => !prospect?.techniques_used?.includes(t.id)
  );

  const suggestedTechnique = unusedTechniques[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gba-green" />
      </div>
    );
  }

  if (!prospect) return null;

  const progressPercent = ((prospect.techniques_used?.length || 0) / 32) * 100;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/prospects")}
          className="text-slate-400 hover:text-white"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">
              {prospect.name}
            </h1>
            <span
              className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(
                prospect.status
              )}`}
            >
              {prospect.status}
            </span>
          </div>
          {prospect.company && (
            <p className="text-slate-400 mt-1">{prospect.company}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setEditDialogOpen(true)}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="edit-prospect-button"
          >
            <Edit className="w-4 h-4" />
            <span className="hidden sm:inline ml-2">Edit</span>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="border-red-800 text-red-400 hover:bg-red-900/30"
                data-testid="delete-prospect-button"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#1E293B] border-slate-700">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Delete Prospect?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  This will permanently delete {prospect.name} and all their follow-up history.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-slate-700 text-white border-none">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Contact Info & Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-[#1E293B] border-slate-700 lg:col-span-2">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {prospect.phone && (
                <a
                  href={`tel:${prospect.phone}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-gba-green transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm truncate">{prospect.phone}</span>
                </a>
              )}
              {prospect.email && (
                <a
                  href={`mailto:${prospect.email}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-gba-green transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm truncate">{prospect.email}</span>
                </a>
              )}
              {prospect.source && (
                <div className="flex items-center gap-2 text-slate-400">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">{prospect.source}</span>
                </div>
              )}
              {prospect.follow_up_by && (
                <div className="flex items-center gap-2 text-amber-400">
                  <CalendarIcon className="w-4 h-4" />
                  <span className="text-sm">Due: {prospect.follow_up_by}</span>
                </div>
              )}
            </div>
            {prospect.notes && (
              <p className="mt-4 text-slate-400 text-sm bg-slate-800/50 p-3 rounded-lg">
                {prospect.notes}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-sm">Techniques Used</span>
              <span className="font-heading text-2xl font-bold text-white">
                {prospect.techniques_used?.length || 0}
                <span className="text-slate-500 text-lg">/32</span>
              </span>
            </div>
            <Progress value={progressPercent} className="h-3 bg-slate-700" />
            <p className="text-xs text-slate-500 mt-2">
              {Math.round(progressPercent)}% complete
            </p>
          </CardContent>
        </Card>
      </div>

      {/* AI Suggestion */}
      <Card className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-700/50">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-600/30 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">AI Suggestion</h3>
                {aiSuggestion ? (
                  <p className="text-slate-300 text-sm">{aiSuggestion.suggestion}</p>
                ) : suggestedTechnique ? (
                  <p className="text-slate-400 text-sm">
                    Try using "{suggestedTechnique.name}" - {suggestedTechnique.description}
                  </p>
                ) : (
                  <p className="text-slate-400 text-sm">
                    You've used all techniques! Great job! Consider reusing effective ones.
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={getAISuggestion}
              disabled={loadingAI}
              className="border-purple-600 text-purple-400 hover:bg-purple-900/30 flex-shrink-0"
              data-testid="ai-suggest-button"
            >
              {loadingAI ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1" /> Get Suggestion
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Techniques & Timeline */}
      <Tabs defaultValue="techniques" className="w-full">
        <TabsList className="bg-[#1E293B] border border-slate-700 p-1">
          <TabsTrigger
            value="techniques"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="techniques-tab"
          >
            32 Techniques
          </TabsTrigger>
          <TabsTrigger
            value="timeline"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="timeline-tab"
          >
            Timeline ({followups.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="techniques" className="mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {techniques.map((technique) => {
              const isUsed = prospect.techniques_used?.includes(technique.id);
              return (
                <Card
                  key={technique.id}
                  className={`cursor-pointer transition-all ${
                    isUsed
                      ? "bg-gba-green/20 border-gba-green"
                      : "bg-[#1E293B] border-slate-700 hover:border-slate-500"
                  }`}
                  onClick={() => {
                    setSelectedTechnique(technique);
                    setTechniqueDialogOpen(true);
                  }}
                  data-testid={`technique-card-${technique.id}`}
                >
                  <CardContent className="p-4 text-center">
                    <div
                      className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                        isUsed ? "bg-gba-green text-white" : "bg-slate-700 text-slate-300"
                      }`}
                    >
                      {isUsed ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        getTechniqueIcon(technique.icon)
                      )}
                    </div>
                    <p className="text-sm font-medium text-white truncate">
                      {technique.name}
                    </p>
                    {isUsed && (
                      <span className="text-xs text-gba-green mt-1 block">Used</span>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          {followups.length === 0 ? (
            <Card className="bg-[#1E293B] border-slate-700 border-dashed">
              <CardContent className="p-8 text-center">
                <p className="text-slate-400">No follow-ups logged yet</p>
                <p className="text-slate-500 text-sm mt-1">
                  Select a technique above to log your first follow-up
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {followups.map((followup, index) => {
                const technique = getTechniqueById(followup.technique_id);
                return (
                  <Card
                    key={followup.id}
                    className="bg-[#1E293B] border-slate-700"
                    data-testid={`timeline-item-${followup.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-gba-green/20 flex items-center justify-center flex-shrink-0">
                          {technique ? getTechniqueIcon(technique.icon) : <MessageSquare className="w-5 h-5 text-gba-green" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="font-medium text-white">
                              {technique?.name || "Follow-up"}
                            </h4>
                            <span className="text-xs text-slate-500">
                              {new Date(followup.date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300">
                              {followup.channel}
                            </span>
                          </div>
                          {followup.notes && (
                            <p className="text-sm text-slate-400 mt-2">
                              {followup.notes}
                            </p>
                          )}
                          {followup.next_follow_up_date && (
                            <p className="text-xs text-amber-400 mt-2">
                              Next follow-up: {followup.next_follow_up_date}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Technique Dialog */}
      <Dialog open={techniqueDialogOpen} onOpenChange={setTechniqueDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedTechnique && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 text-white">
                  <div className="w-10 h-10 rounded-full bg-gba-green/20 flex items-center justify-center">
                    {getTechniqueIcon(selectedTechnique.icon)}
                  </div>
                  {selectedTechnique.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <p className="text-slate-300">{selectedTechnique.description}</p>

                <Tabs defaultValue="whatsapp">
                  <TabsList className="bg-slate-800">
                    <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
                    <TabsTrigger value="email">Email</TabsTrigger>
                  </TabsList>
                  <TabsContent value="whatsapp" className="mt-3">
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">
                        {selectedTechnique.whatsapp_template.replace(
                          /{name}/g,
                          prospect.name
                        )}
                      </p>
                    </div>
                    <Button
                      onClick={() => openWhatsApp(selectedTechnique.whatsapp_template)}
                      className="mt-3 w-full bg-green-600 hover:bg-green-700"
                      disabled={!prospect.phone}
                      data-testid="send-whatsapp-button"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send via WhatsApp
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </TabsContent>
                  <TabsContent value="email" className="mt-3">
                    <div className="bg-slate-800/50 p-4 rounded-lg">
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">
                        {selectedTechnique.email_template.replace(
                          /{name}/g,
                          prospect.name
                        )}
                      </p>
                    </div>
                    <Button
                      onClick={() => openEmail(selectedTechnique.email_template)}
                      className="mt-3 w-full bg-blue-600 hover:bg-blue-700"
                      disabled={!prospect.email}
                      data-testid="send-email-button"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send via Email
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </TabsContent>
                </Tabs>

                <div className="border-t border-slate-700 pt-4">
                  <h4 className="font-medium text-white mb-3">Log this follow-up</h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-300">Channel Used</Label>
                      <Select
                        value={logFormData.channel}
                        onValueChange={(value) =>
                          setLogFormData({ ...logFormData, channel: value })
                        }
                      >
                        <SelectTrigger className="bg-[#0F172A] border-slate-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-slate-700">
                          {CHANNEL_OPTIONS.map((ch) => (
                            <SelectItem key={ch} value={ch}>
                              {ch}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-slate-300">Notes</Label>
                      <textarea
                        value={logFormData.notes}
                        onChange={(e) =>
                          setLogFormData({ ...logFormData, notes: e.target.value })
                        }
                        placeholder="How did it go?"
                        className="w-full h-20 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white placeholder:text-slate-600 resize-none"
                        data-testid="followup-notes-input"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300">Next Follow-up Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left bg-[#0F172A] border-slate-700 text-white"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {nextFollowUpDate
                              ? format(nextFollowUpDate, "PPP")
                              : "Set next follow-up"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#1E293B] border-slate-700">
                          <Calendar
                            mode="single"
                            selected={nextFollowUpDate}
                            onSelect={setNextFollowUpDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <Button
                      onClick={handleLogFollowup}
                      disabled={saving}
                      className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-3 uppercase tracking-wide shadow-glow"
                      data-testid="log-followup-button"
                    >
                      {saving ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        "Log & Save"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">
              Edit Prospect
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={editFormData.name || ""}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                className="bg-[#0F172A] border-slate-700 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Company</Label>
              <Input
                value={editFormData.company || ""}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, company: e.target.value })
                }
                className="bg-[#0F172A] border-slate-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Phone</Label>
                <Input
                  value={editFormData.phone || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, phone: e.target.value })
                  }
                  className="bg-[#0F172A] border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input
                  value={editFormData.email || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, email: e.target.value })
                  }
                  className="bg-[#0F172A] border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Source</Label>
                <Input
                  value={editFormData.source || ""}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, source: e.target.value })
                  }
                  className="bg-[#0F172A] border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(value) =>
                    setEditFormData({ ...editFormData, status: value })
                  }
                >
                  <SelectTrigger className="bg-[#0F172A] border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-slate-700">
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Follow Up By</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left bg-[#0F172A] border-slate-700 text-white"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {followUpDate ? format(followUpDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1E293B] border-slate-700">
                  <Calendar
                    mode="single"
                    selected={followUpDate}
                    onSelect={setFollowUpDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Notes</Label>
              <textarea
                value={editFormData.notes || ""}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, notes: e.target.value })
                }
                className="w-full h-24 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 uppercase tracking-wide"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProspectDetail;
